import type { Error as RapidWorksRaptorCommonErrorsError } from '@/libs/api-rest/generated/model/error';
import type { ProblemDetails } from '@/libs/api-rest/generated/model/problemDetails';

type RestApiError = RapidWorksRaptorCommonErrorsError | ProblemDetails;

/**
 * Extracts the error message from REST API error types
 *
 * Handles both RapidWorksRaptorCommonErrorsError and ProblemDetails types.
 * Priority order:
 * 1. RapidWorksRaptorCommonErrorsError.errorMessage
 * 2. ProblemDetails.detail
 * 3. ProblemDetails.title
 * 4. Default message
 *
 * @param error - The REST API error to extract message from
 * @param defaultMessage - Default message if no error message is found (default: 'Unknown error occurred')
 * @returns The error message string
 *
 * @example
 * ```ts
 * onError: (error) => {
 *   const message = extractRestApiErrorMessage(error, 'Failed to create contact');
 *   console.error(message);
 * }
 * ```
 */
export const extractRestApiErrorMessage = (error: RestApiError | unknown, defaultMessage: string = 'Unknown error occurred'): string => {
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  if ('errorMessage' in error && typeof error.errorMessage === 'string' && error.errorMessage) {
    return error.errorMessage;
  }

  if ('detail' in error && typeof error.detail === 'string' && error.detail) {
    return error.detail;
  }

  if ('title' in error && typeof error.title === 'string' && error.title) {
    return error.title;
  }

  return defaultMessage;
};

/**
 * Extracts the status code from REST API error types
 *
 * Handles both RapidWorksRaptorCommonErrorsError and ProblemDetails types.
 * Priority order:
 * 1. RapidWorksRaptorCommonErrorsError.statusCode
 * 2. ProblemDetails.status
 *
 * @param error - The REST API error to extract status code from
 * @returns The status code number or undefined if not found
 *
 * @example
 * ```ts
 * onError: (error) => {
 *   const statusCode = extractRestApiErrorStatusCode(error);
 *   if (statusCode === 404) {
 *     // Handle not found
 *   }
 * }
 * ```
 */
export const extractRestApiErrorStatusCode = (error: RestApiError | unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode;
  }

  if ('status' in error && typeof error.status === 'number') {
    return error.status;
  }

  return undefined;
};

/**
 * Extracts error details from REST API error types
 *
 * Returns a structured object with message, statusCode, and errorType (if available).
 *
 * @param error - The REST API error to extract details from
 * @param defaultMessage - Default message if no error message is found (default: 'Unknown error occurred')
 * @returns Object with message, statusCode, and errorType
 *
 * @example
 * ```ts
 * onError: (error) => {
 *   const details = extractRestApiErrorDetails(error);
 *   console.error('Error details:', details);
 * }
 * ```
 */
export const extractRestApiErrorDetails = (
  error: RestApiError | unknown,
  defaultMessage: string = 'Unknown error occurred'
): {
  message: string;
  statusCode?: number;
  errorType?: RapidWorksRaptorCommonErrorsError['errorType'];
} => {
  if (!error || typeof error !== 'object') {
    return { message: defaultMessage };
  }

  const message = extractRestApiErrorMessage(error, defaultMessage);
  const statusCode = extractRestApiErrorStatusCode(error);

  let errorType: RapidWorksRaptorCommonErrorsError['errorType'] | undefined;
  if ('errorType' in error) {
    const rapidWorksError = error as RapidWorksRaptorCommonErrorsError;
    errorType = rapidWorksError.errorType;
  }

  return {
    message,
    statusCode,
    errorType
  };
};
